import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReportHasRun, useReportRunKey } from './reportRunStateContext'
import { toast } from '@store/toastStore'
import reportJobService from '@api/v1/reportJobService'
import reportScheduleService from '@api/v1/reportScheduleService'
import { specializedJobsOn } from '@api/v1/reportJobRun'
import { useInvalidateSchedules } from '@hooks/useSchedules'
import ReportFilterPanel from './ReportFilterPanel'
import ReportBand from './ReportBand'
import QueueReportModal from './queue/QueueReportModal'
import {
  toFilterColumns,
  toFilterValues,
  applyFilterChange,
  resetFilterFields,
} from './specializedFilterFields'

// Spec 194: a page that declares no server-export formats is link-only (and, spec 200,
// offers no other recipients).
const NO_FORMATS = Object.freeze([])

/**
 * CC-50013 G2 + CC-50148 — one filter bar for the specialized report pages.
 *
 * Renders a page's filters through `ReportFilterPanel` (the same metadata-driven
 * registry the 119 generic reports use) from the declarative field specs in
 * `specializedFilterFields.js`, and adds the two things no specialized page had:
 *
 *   - **Reset.** Restores this bar's own fields to the page's declared defaults
 *     and leaves every other wire key alone.
 *   - **The split Run/Queue control (CC-50148).** Run now · run in the
 *     background · queue for later · set up a recurring schedule.
 *
 * ## What this deliberately does NOT change
 *
 * The wire. Each page still owns its filter object and its own run call; this
 * component only reads and writes that object through the per-field `get`/`set`
 * declared alongside it. The reason is CC-49833 / CC-50020: CB's Newtonsoft
 * deserialization silently ignores unknown properties, so a filter key this
 * refactor renamed or dropped would bind to nothing, the proc would run
 * UNSCOPED, and no test, log or screen would say so. Every migrated page
 * therefore ships a wire-contract test asserting the exact keys it sends.
 *
 * ## The queue control is gated on the report ACTUALLY being queueable
 *
 * It renders only when `specializedJobsOn()` AND the caller supplied a `queue`
 * spec. A control that renders but whose job fails for that report kind is worse
 * than no control — that is the CC-49833 false-QA-signal shape. `queue.endpoint`
 * must be the SAME `{ method, path, body }` the page's own run posts, so a
 * queued run is the run you were about to make.
 */
const SpecializedFilterBar = ({
  fields = [],
  filters = {},
  onFiltersChange,
  defaults,
  onRun,
  isLoading,
  // Separate from `isLoading`: a page that cannot run YET (an inverted date
  // range, no budget chosen, an empty category selection) must show a DISABLED
  // "Run report", not the word "Running…". Several pages previously folded their
  // guard into the loading flag, which claimed a blocked page was already running.
  disabled,
  runLabel,
  hasRun,
  actions,
  /**
   * Omit to keep the plain Run button (no queue).
   *
   * Two shapes, matching the two job kinds the backend actually supports:
   *
   *   specialized — `{ reportId, name, actionKey, endpoint(filters, { mode }) }`
   *                 A POST replay: `endpoint` returns `{ method, path, body }`.
   *                 `mode` is 'now' | 'later' | 'schedule'. Optional
   *                 `scheduleDatesNote(filters)` replaces the modal's generic "the
   *                 dates stay where you put them" sentence when that is not true
   *                 (spec 192: Exago, a GET whose as-of can be the run's own day).
   *                 Optional `timeoutMs`: how long the job/schedule create may take,
   *                 for a report whose create door answers slowly (spec 209: an
   *                 Advanced Report's door can read the user's Exago list first).
   *                 Omitted → the create calls are sent exactly as before.
   *
   *   accounting  — `{ reportId, name, kind: 'accounting', accountingKey,
   *                    endpoint(filters), filters(filters) }`
   *                 A GET replay whose params ride in the query string, plus a
   *                 separate `filters` value used as the dedupe input
   *                 (canonicalized server-side). `accountingKey` also drives the
   *                 data-security class server-side — P&L is firm-shareable
   *                 while GL / Balance Sheet / Trial Balance / GL-Budget stay
   *                 per-user because of bank-account security.
   *
   * Getting this wrong is not cosmetic: a job created with the wrong kind replays
   * the wrong path and fails, which is the CC-49833 shape. Always mirror what the
   * page's own service already sends rather than inventing a spec.
   */
  queue,
}) => {
  const [scheduleWhen, setScheduleWhen] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const invalidateSchedules = useInvalidateSchedules()
  /* spec 092: the shell knows whether a run has landed; the page that builds this
     bar does not. Reading it here gives all 20 specialized pages the two-state run
     control without touching any of them. An explicit `hasRun` prop still wins, so
     a page that knows better is never overridden. */
  const ambientHasRun = useReportHasRun()

  const columns = useMemo(() => toFilterColumns(fields), [fields])
  const values = useMemo(() => toFilterValues(fields, filters), [fields, filters])

  const onChange = useCallback(
    (columnId, value) => {
      onFiltersChange((prev) => applyFilterChange(fields, columnId, value, prev))
    },
    [fields, onFiltersChange]
  )

  /*
   * R3 (2026-08-28) — the stale signal, on the 21 specialized pages too.
   *
   * Section E4 renamed this control "Reset filters" and taught the GENERIC
   * engine to say that the grid no longer answers what the panel shows. The
   * rename shipped to all 140 reports; the signal shipped to 119. On the 21
   * pages that difference is worse than it sounds, because they are A/R, A/P,
   * Balance Sheet, Trial Balance and the General Ledger — the reports whose
   * figures ARE the financial statements. A user got a more confident button
   * name, changed filter text, unchanged money, and nothing announced.
   *
   * Deliberately mirrors ReportView rather than inventing a second design:
   * gated on the values actually MOVING, cleared at the start of the next run,
   * and the run control drops back to "Run report" while it holds.
   */
  const [resetSinceRun, setResetSinceRun] = useState(false)

  const onReset = useCallback(() => {
    if (!defaults) return
    /*
     * Gated on the values ACTUALLY moving, exactly as ReportView is: resetting a
     * page that already sits at its defaults changes nothing, and claiming a
     * grid of financial figures is stale when it is not is a false statement
     * about money — it also flips a good "Refresh" back to "Run report" for no
     * reason.
     *
     * Compared through `toFilterValues`, i.e. over THIS BAR'S OWN fields, not
     * over the whole wire object: `resetFilterFields` deliberately leaves wire
     * keys that have no control alone, so a whole-object compare would be
     * answering a different question. `f.get` returns a string for every field
     * type in specializedFilterFields.js, so a key-by-key `!==` is a complete
     * comparison and needs no stable stringify.
     *
     * Computed from the `filters` prop rather than inside the updater: an
     * impure state updater is double-invoked under StrictMode (the same reason
     * ReportView.jsx reads its pre-reset values from state).
     */
    const nextValues = toFilterValues(fields, resetFilterFields(fields, filters, defaults))
    setResetSinceRun(fields.some((f) => nextValues[f.key] !== values[f.key]))
    onFiltersChange((prev) => resetFilterFields(fields, prev, defaults))
  }, [fields, defaults, filters, values, onFiltersChange])

  /*
   * ── THE CHOKE POINT, and why this button is not it ────────────────────────
   *
   * SUPERSEDED (2026-08-28) — this comment used to say "every run path this bar
   * owns passes through here, so there is no second place that has to remember
   * to clear the flag." The first clause was true and the conclusion did not
   * follow: the bar does not own every run. Balance Sheet, Trial Balance,
   * General Ledger and P&L re-run themselves on any filter change
   * (`useAutoRunOnFilterChange`, 300ms debounce) and never call `onRun`. A reset
   * on those four therefore auto-ran, the figures came back FRESH, and the band
   * went on saying the grid was stale for as long as the page stayed open, with
   * the run control pinned at "Run report". Those four pages are the financial
   * statements. A permanent false staleness warning there is worse than none —
   * it trains the reader to ignore the one signal that means the figures do not
   * match the filters.
   *
   * The generic engine clears inside `runReportWith`, which every one of its run
   * paths enters. The equivalent seam here is the page's own loading flag, and
   * the `isLoading` effect below is now the real choke point: all 20 pages pass
   * `isLoading={running}`, the same `running` the shell watches, so a run this
   * bar never fired still clears the flag. Nothing is keyed on a page NAME, so a
   * 22nd specialized page that auto-runs is covered the day it lands.
   *
   * This wrapper is KEPT as well, and the two cannot drift because both only
   * ever write `false`: it covers the case the transition cannot see — a page
   * whose run resolves without ever exposing an `isLoading === true` commit
   * (a cached or synchronous result). Losing that would reintroduce this very
   * blocker from the other side. A run the page then refuses (an inverted range,
   * no budget chosen) still leaves the grid correctly marked stale, because
   * `disabled` stops the panel calling this at all.
   */
  const runAndClearStale = useCallback((...args) => {
    setResetSinceRun(false)
    return onRun?.(...args)
  }, [onRun])

  /*
   * Keyed on the false→true TRANSITION, not on truthiness: a bar that mounts
   * mid-run (AR reopening a saved result starts with `running: !!jobId`) must
   * not re-clear on every unrelated re-render afterwards.
   *
   * Spec 131 — AND on a new `runKey`. The latest-run gate (useLatestRun) keeps
   * `isLoading` TRUE across a chain of superseding runs, so a Reset clicked while
   * a run was in flight on Balance Sheet, Trial Balance, General Ledger or P&L
   * (which re-run themselves and never pass through `runAndClearStale`) never saw
   * a false→true edge: the band said "the results below were run with the
   * previous filters" over fresh statements, with the control pinned at "Run
   * report", for as long as the page stayed open. `runKey` changes only when a
   * run STARTS — never at a refusal or an invalidate — so a refused run still
   * leaves the grid correctly marked stale.
   */
  const runKey = useReportRunKey()
  const wasLoadingRef = useRef(false)
  const lastRunKeyRef = useRef(runKey)
  useEffect(() => {
    const wasLoading = wasLoadingRef.current
    wasLoadingRef.current = !!isLoading
    const runStarted = runKey !== lastRunKeyRef.current
    lastRunKeyRef.current = runKey
    if ((!wasLoading && isLoading) || runStarted) setResetSinceRun(false)
  }, [isLoading, runKey])

  /* Computed ONCE and read by both consumers below — the run control's prop and
     the stale band's gate. Written out twice they could drift, which is exactly
     the bug ReportView's `hasRun` const was introduced to prevent. */
  const resultOnScreen = hasRun ?? ambientHasRun

  // Only offer the queue paths where there IS a queue to run into.
  const queueOn = !!queue && specializedJobsOn()
  // Spec 194/200: the formats this page's own server export produces — the owner's
  // attachment, and whether other recipients can be offered at all.
  const attachFormats = queue?.attachFormats ?? NO_FORMATS
  const canAttach = attachFormats.length > 0

  const submit = useCallback(
    async (mode, payload, delivery = {}) => {
      if (!queue) return
      setSubmitting(true)
      try {
        // Resolved from the filters on screen at submit time — the same request
        // the page's own Run would make. `mode` lets a page whose run route can
        // resolve its own date at run time (Exago, spec 192) ask for that only on a
        // recurring schedule; every other page ignores it.
        const endpoint = queue.endpoint(filters, { mode })
        // Spec 209: only a page that sets `timeoutMs` changes the create request; every
        // other page's call is exactly what it was (no second argument at all).
        const createConfig = Number.isFinite(queue.timeoutMs) && queue.timeoutMs > 0
          ? [{ timeout: queue.timeoutMs }]
          : []
        const isAccounting = queue.kind === 'accounting'
        const base = isAccounting
          ? {
              kind: 'accounting',
              accountingKey: queue.accountingKey,
              reportId: queue.reportId,
              endpoint,
              // The accounting job path dedupes on the params, not the endpoint
              // body (there isn't one — it is a GET).
              filters: queue.filters ? queue.filters(filters) : undefined,
            }
          : {
              kind: 'specialized',
              actionKey: queue.actionKey,
              reportId: queue.reportId,
              endpoint,
            }
        if (mode === 'schedule') {
          await reportScheduleService.create({
            ...base,
            name: payload.name || queue.name,
            recurrence: payload.recurrence,
            // No rollForward: a specialized report's dates are plain fields on
            // its own filter class, not the generic engine's filter array that
            // carries a preset token, so there is nothing here for the server to
            // re-resolve at fire time. A recurring run repeats the FIXED window
            // on screen. Saying so beats silently implying otherwise —
            // roll-forward for these pages needs a per-page date-field map
            // (recorded on CC-50148).
            retainCount: payload.retainCount,
            deliverEmail: payload.deliverEmail,
            deliverFormat: payload.deliverFormat,
            // Spec 200: other recipients — and the owner's own address, while the modal
            // asks for one (spec 139) — travel as the modal hands them over, on a page
            // whose report can be attached (`attachFormats`), which is the rule cb-api
            // applies to them too. A link-only page never sends either, whatever a modal
            // hands it: the field there is a sentence.
            ...(canAttach && payload.deliverRecipients !== undefined ? { deliverRecipients: payload.deliverRecipients } : {}),
            ...(canAttach && payload.deliverOwnerAddress !== undefined ? { deliverOwnerAddress: payload.deliverOwnerAddress } : {}),
            /*
             * spec 120. Dropped here until now, exactly as on ReportView: the
             * modal computed a visibility choice and this call discarded it, so
             * "Everyone at the firm" saved as `scope='mine'` with a 201 and no
             * sign anything had been ignored. Unlike `rollForward` above, there
             * is nothing kind-specific about scope — `schedules` carries the
             * columns for every kind and cb-api validates a group scope the
             * same way — so this one was a gap, not a decision.
             */
            scope: payload.scope,
            groupId: payload.groupId,
            groupName: payload.groupName,
          }, ...createConfig)
          // One cached key backs both the schedules list and the Queue page's
          // count chip; without this the new schedule needed a hard refresh.
          invalidateSchedules()
          toast.success(`${queue.name || 'Report'} is scheduled`)
        } else {
          await reportJobService.create({
            ...base,
            ...(mode === 'later' ? { scheduledFor: payload } : {}),
            ...(delivery.deliverEmail
              ? { deliverEmail: true, deliverFormat: delivery.deliverFormat }
              : {}),
          }, ...createConfig)
          toast.success(
            mode === 'later'
              ? `${queue.name || 'Report'} is scheduled to run`
              : `${queue.name || 'Report'} is running in the background`
          )
        }
        setScheduleWhen(null)
      } catch (err) {
        toast.error(err?.message || 'Could not queue that report.')
      } finally {
        setSubmitting(false)
      }
    },
    [queue, filters, invalidateSchedules, canAttach]
  )

  return (
    <>
      <ReportFilterPanel
        columns={columns}
        values={values}
        onChange={onChange}
        onRun={runAndClearStale}
        onReset={defaults ? onReset : undefined}
        isLoading={isLoading}
        disabled={disabled}
        runLabel={runLabel}
        /* Subtracted here for the same reason ReportView subtracts it: the
           question the two-state control is really asking is not "is there a
           result" but "does the result answer what the panel currently says". */
        hasRun={resultOnScreen && !resetSinceRun}
        actions={actions}
        // "Run in the background now" opens the queue modal on the "now" tab
        // rather than firing directly, because the email/delivery toggle lives
        // only in that modal — a direct run gave no way to opt into
        // "email me when it's ready" (same reasoning as ReportView).
        onRunBackground={queueOn ? () => setScheduleWhen('now') : undefined}
        onSchedule={queueOn ? setScheduleWhen : undefined}
      />
      {/*
        THE LIVE REGION IS THE WRAPPER, AND THE WRAPPER IS ALWAYS MOUNTED — a
        region inserted into the DOM together with its content is not reliably
        announced, so gating the whole element on `resetSinceRun` would style the
        warning without announcing it. Same shape, and the same reasoning, as
        ReportView's `report-stale-live`.

        `role={null}` on the band is deliberate: ReportBand defaults to
        role="status", itself a polite live region, and nesting one inside this
        wrapper leaves it undefined which region owns the change.

        It sits directly under the panel because that is the whole of what this
        component renders — the grid is `{children}` of the shell, built by the
        page. Below the filters and above the result is still the right side of
        the claim.

        The prose names the control the way the control spells itself (C3), which
        on this path means reading `runLabel` — ReportFilterPanel.jsx:1381 lets an
        explicit one win over the two-state default, and MatterMetricsReport
        passes "Filter". Hardcoding "Run report" would have sent that page's users
        looking for a button that is not on their screen. Note that on that page
        the run-label change is ALSO not a signal (its label is fixed), so there
        the criterion rests on the prose and the ↻ glyph alone.
      */}
      <div aria-live="polite" data-print-hide data-testid="specialized-stale-live">
        {!isLoading && resultOnScreen && resetSinceRun && (
          <ReportBand variant="stale" role={null} className="mb-4">
            <span className="font-semibold">Filters were reset</span>
            {' — '}the results below were run with the previous filters and have not
            been updated. Click {runLabel || 'Run report'} to apply the filters shown above.
          </ReportBand>
        )}
      </div>
      {scheduleWhen && (
        <QueueReportModal
          // The subtitle's "#id" is a catalog number; an Exago id (`exago/<guid>`) is not one.
          report={{ id: /^\d+$/.test(String(queue.reportId ?? '')) ? queue.reportId : undefined, name: queue.name }}
          // The SAME synthesized columns the bar renders, so the modal shows this
          // report's real filters and edits ride back through the same per-field
          // mapping. Without them the modal's filter column would be empty.
          columns={columns}
          values={values}
          onChange={onChange}
          initialWhen={scheduleWhen}
          /*
           * spec 120 §7. These pages cannot store a roll-forward token — the
           * `create` call above deliberately omits `rollForward` (see its own
           * note), and even if it sent one the runner only re-resolves the
           * token on the GENERIC path. Their synthesized date field is still
           * spelled `filterTypeName: 'DATE'` though, and its empty value
           * reverse-matches the All Dates preset, so the modal would otherwise
           * render the toggle ON and promise "each run reports on All Dates as
           * of the day it fires" on all 21 specialized reports — with the
           * CC-50232 warning unable to contradict it, because that warning
           * needs the `filters` array these hosts do not have. No control is
           * the honest UI until CC-50148's per-page date-field map exists.
           */
          rollForwardSupported={false}
          // Spec 192: what a recurring run's dates will be, when the page knows better
          // than the generic "they stay where you put them" (Exago's rolling as-of).
          scheduleDatesNote={queue.scheduleDatesNote ? queue.scheduleDatesNote(filters) : undefined}
          // Spec 194: the owner's own copy may carry this page's SERVER export, in the
          // formats it produces (`queue.attachFormats`). cb-api's mailer requests that
          // same export route as the owner (`jobs/specializedExport.js`). A page that
          // declares none stays link-only, as before.
          // Spec 200: and other recipients follow the same rule — offered wherever that
          // file exists, since they get the one file the owner gets (`attachSupported`
          // decides recipients). A link-only page still shows the sentence instead.
          attachSupported={canAttach}
          ownAttachFormats={attachFormats}
          isSubmitting={submitting}
          onQueueNow={(delivery) => submit('now', null, delivery)}
          onQueueLater={(at, delivery) => submit('later', at, delivery)}
          onSchedule={(cfg) => submit('schedule', cfg)}
          onClose={() => setScheduleWhen(null)}
        />
      )}
    </>
  )
}

export default SpecializedFilterBar
